import React, { useState } from 'react';
import { DvprItem, AltDataPoint } from '../types';
import { generateProjectReport } from '../services/geminiService';
import { FileText, Download, Share2, Sparkles } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';

interface ReportingProps {
  dvprItems: DvprItem[];
  altData: AltDataPoint[];
}

export const Reporting: React.FC<ReportingProps> = ({ dvprItems, altData }) => {
  const { exportProject } = useAppContext();
  const [reportText, setReportText] = useState('');
  const [generating, setGenerating] = useState(false);

  const handleDownload = async () => {
    const url = await exportProject('pdf');
    window.open(url, '_blank');
  };

  const handleShare = async () => {
    const url = await exportProject('json');
    navigator.clipboard.writeText(url);
    console.log('🔗 Share link copied to clipboard');
  };

  const handleGenerate = async () => {
    setGenerating(true);
    const text = await generateProjectReport(dvprItems, altData);
    setReportText(text);
    setGenerating(false);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Project Reporting</h2>
          <p className="text-slate-500">Generate executive summaries and compliance documents.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg shadow-md shadow-indigo-200 hover:shadow-lg hover:from-indigo-700 hover:to-violet-700 transition-all font-medium disabled:opacity-50"
          >
            {generating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles size={18} />
                Generate AI Report
              </>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 min-h-[600px] flex flex-col">
        {/* Document Header */}
        <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white border border-slate-200 rounded shadow-sm">
              <FileText size={20} className="text-slate-500" />
            </div>
            <span className="font-semibold text-slate-700">Executive_Summary_Rev01.md</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleDownload}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
              title="Download PDF"
            >
              <Download size={18} />
            </button>
            <button
              onClick={handleShare}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
              title="Share"
            >
              <Share2 size={18} />
            </button>
          </div>
        </div>

        {/* Document Content */}
        <div className="flex-1 p-8 lg:p-12 overflow-y-auto">
          {!reportText ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-4">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center">
                <Sparkles size={32} className="text-slate-300" />
              </div>
              <p>Click &quot;Generate AI Report&quot; to create a summary based on current project data.</p>
            </div>
          ) : (
            <article className="prose prose-slate max-w-none">
              {/* Simple renderer for the markdown text */}
              {reportText.split('\n').map((line, i) => {
                if (line.startsWith('# ')) return <h1 key={i} className="text-3xl font-bold mb-4 text-slate-900">{line.replace('# ', '')}</h1>
                if (line.startsWith('## ')) return <h2 key={i} className="text-xl font-bold mt-6 mb-3 text-slate-800">{line.replace('## ', '')}</h2>
                if (line.startsWith('- ')) return <li key={i} className="ml-4 list-disc text-slate-700">{line.replace('- ', '')}</li>
                return <p key={i} className="mb-2 text-slate-700 leading-relaxed">{line}</p>
              })}
            </article>
          )}
        </div>
      </div>
    </div>
  );
};